import { useState } from 'react'
import { BookOpen, Download, FileText, PlayCircle } from 'lucide-react'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import FieldPrimerDialog from 'features/Help/FieldPrimerDialog'
import { fieldPrimer, primerPages } from 'content/help/fieldPrimer'

/**
 * Where a help document is published, in both of its forms.
 *
 * **Support is a section, not this page.** The request was for a folder that
 * grows, so the shape here is a list of documents with one entry in it rather
 * than a page about the primer. The second document should need no layout work.
 *
 * Note `/admin/support` is already the impersonation tool, which is why Help
 * Docs takes a child path rather than that one. The impersonation tool has
 * never been in the sidebar and stays out of it.
 *
 * **Not gated on `isAdmin`.** The Admin group in the sidebar is visible to
 * every desktop user, and a setup guide that only administrators can read would
 * defeat its own purpose: the people who need it are new users.
 */

const PDF_HREF = '/help/mydealteams-field-primer.pdf'

const HelpDocsCN = () => {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [primerOpen, setPrimerOpen] = useState(false)

	// Read off the content rather than written here, so the summary on this card
	// cannot describe a document that has since changed length.
	const stepCount = primerPages().length

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex flex-1 flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<main className="flex-1 overflow-y-auto p-6">
					<div className="mx-auto max-w-[800px]">
						<h2 className="text-2xl font-semibold text-gray-800">Help Docs</h2>
						<p className="mt-1 text-sm text-gray-600">
							Guides for people using MYDealTeams. Each one can be walked through on screen or
							downloaded as a PDF to print or send on.
						</p>

						<article className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
							<div className="flex items-start gap-4">
								<span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
									<BookOpen className="h-5 w-5" />
								</span>
								<div className="min-w-0 flex-1">
									<h3 className="text-lg font-semibold text-gray-900">{fieldPrimer.title}</h3>
									<p className="mt-0.5 text-sm text-gray-600">{fieldPrimer.subtitle}</p>

									<dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
										<div>
											<dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
												For
											</dt>
											<dd className="text-gray-700">{fieldPrimer.audience}</dd>
										</div>
										<div>
											<dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
												Length
											</dt>
											<dd className="text-gray-700">
												{stepCount} steps, about {fieldPrimer.estimatedMinutes} minutes
											</dd>
										</div>
										<div>
											<dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
												Version
											</dt>
											<dd className="text-gray-700">
												{fieldPrimer.version}, {fieldPrimer.updated}
											</dd>
										</div>
									</dl>

									<div className="mt-5 flex flex-wrap gap-2">
										<button
											type="button"
											onClick={() => setPrimerOpen(true)}
											className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
										>
											<PlayCircle className="h-4 w-4" />
											Walk through it
										</button>
										{/*
											A plain link, not a fetch. The file is a static asset, so the
											browser's own download handling is both simpler and better
											behaved than anything built on top of it.
										*/}
										<a
											href={PDF_HREF}
											download
											className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
										>
											<Download className="h-4 w-4" />
											Download PDF
										</a>
										<a
											href={PDF_HREF}
											target="_blank"
											rel="noreferrer"
											className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
										>
											<FileText className="h-4 w-4" />
											Open in a new tab
										</a>
									</div>
								</div>
							</div>
						</article>

						{/*
							Said out loud rather than left as tribal knowledge. The PDF is a
							build artefact: editing the content without rebuilding leaves the
							download contradicting the walkthrough, which is the exact failure
							the shared content file exists to prevent.
						*/}
						<p className="mt-4 text-xs text-gray-500">
							Both forms are rendered from{' '}
							<code className="rounded bg-gray-100 px-1 py-0.5 text-[11px]">
								frontend/src/content/help/fieldPrimer.json
							</code>
							. After editing it, rebuild the PDF with{' '}
							<code className="rounded bg-gray-100 px-1 py-0.5 text-[11px]">
								node backend/scripts/buildHelpDocs.js
							</code>
							.
						</p>
					</div>
				</main>
			</div>

			<FieldPrimerDialog open={primerOpen} onClose={() => setPrimerOpen(false)} />
		</div>
	)
}

export default HelpDocsCN
